import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem,
} from '@mui/material';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

export default function AssetFormModal({ open, onClose, onSubmit, selectedRow, action, users }) {
  const formik = useFormik({
    initialValues: {
      name: '',
      type: '',
      assignedTo: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      type: Yup.string().required('Type is required'),
      assignedTo: Yup.string().required('Assigned To is required'),
    }),
    onSubmit: async (values) => {
      try {
        await onSubmit(values);
        onClose();
      } catch (error) {
        toast.error(error?.response?.data?.message ?? error.message);
      }
    },
    enableReinitialize: true, // Important for updating values in edit mode
  });

  useEffect(() => {
    if (action === 'edit' && selectedRow?.original) {
      formik.setValues({
        name: selectedRow.original.name || '',
        type: selectedRow.original.type || '',
        assignedTo: selectedRow.original.assignedTo?.toString() || '',
      });
    } else {
      formik.resetForm();
    }
  }, [selectedRow, action, open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{action === 'edit' ? 'Edit Asset' : 'Create Asset'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Asset Name"
            name="name"
            type="text"
            fullWidth
            variant="outlined"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Asset Type"
            name="type"
            type="text"
            fullWidth
            variant="outlined"
            value={formik.values.type}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.type && Boolean(formik.errors.type)}
            helperText={formik.touched.type && formik.errors.type}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Assigned To"
            name="assignedTo"
            select
            fullWidth
            variant="outlined"
            value={formik.values.assignedTo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.assignedTo && Boolean(formik.errors.assignedTo)}
            helperText={formik.touched.assignedTo && formik.errors.assignedTo}
            sx={{ mb: 2 }}
          >
            {users?.map((user) => (
              <MenuItem key={user.id} value={user.id.toString()}>
                {`${user.userName}`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            {action === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

AssetFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  action: PropTypes.string,
  users: PropTypes.array.isRequired,
};
